<script setup lang="ts">
import type {
  Attachment,
  FineUploaderUpload,
  WorkExperienceEditPayload,
} from '@/models/current-student-advanced-standing-form/api'
import type { AlertTone } from '@/models/current-student-advanced-standing-form/modalAlert'
import CrudModalShell from '@/components/current-student-advanced-standing-form/shared/CrudModalShell.vue'
import ModalAlert from '@/components/current-student-advanced-standing-form/shared/ModalAlert.vue'
import WorkExperienceAttachments from '@/components/current-student-advanced-standing-form/modals/work-experience/WorkExperienceAttachments.vue'
import WorkExperienceDetailsFieldset from '@/components/current-student-advanced-standing-form/modals/work-experience/WorkExperienceDetailsFieldset.vue'
import WorkExperienceEvidenceInstructions from '@/components/current-student-advanced-standing-form/modals/work-experience/WorkExperienceEvidenceInstructions.vue'

defineProps<{
  shown: boolean
  active: boolean
  currentWorkExperience: Partial<WorkExperienceEditPayload> | null
  workExperienceYear: string
  workExperienceShortDescription: string
  workExperienceLongDescription: string
  currentWorkExperienceAttachments: Attachment[]
  workExperienceAttachmentDeleteIds: number[]
  showWorkExperienceFineUploader: boolean
  visibleWorkExperienceNewAttachments: FineUploaderUpload[]
  workExperienceUploadDropActive: boolean
  workExperienceUploadAlertMessage: string
  errorMessage: string
  alertTone: AlertTone
  saving: boolean
  uploadThumbnailUrl: (attachment: FineUploaderUpload) => string
}>()

const emit = defineEmits<{
  'update:workExperienceYear': [value: string]
  'update:workExperienceShortDescription': [value: string]
  'update:workExperienceLongDescription': [value: string]
  workExperienceLongInputChange: []
  stageWorkExperienceAttachmentDelete: [attachment: Attachment]
  workExperienceUploadDragEnter: [event: DragEvent]
  workExperienceUploadDragOver: [event: DragEvent]
  workExperienceUploadDragLeave: [event: DragEvent]
  workExperienceUploadDrop: [event: DragEvent]
  triggerUploadInput: [event: MouseEvent]
  uploadButtonKeydown: [event: KeyboardEvent]
  uploadButtonHover: [event: MouseEvent, hover: boolean]
  uploadButtonFocus: [event: FocusEvent, focused: boolean]
  workExperienceAttachmentSelected: [event: Event]
  cancelStagedUpload: [attachment: FineUploaderUpload]
  retryWorkExperienceAttachment: [attachment: FineUploaderUpload]
  deleteStagedWorkExperienceAttachment: [attachment: FineUploaderUpload]
  closeWorkExperienceUploadAlert: []
  delete: []
  close: []
  save: []
}>()
</script>

<template>
  <CrudModalShell
    modal-id="WorkExperienceModal"
    title="Edit Work Experience"
    :shown="shown"
    :active="active"
    delete-button-id="DeleteWorkExperienceModalBtn"
    :delete-hidden="currentWorkExperience?.submission_work_experience_id == null || currentWorkExperience?.experience_verified === 2"
    save-button-id="SaveWorkExperienceModalBtn"
    :saving="saving"
    cancel-button-class="btn btn-primary"
    @close="emit('close')"
    @delete="emit('delete')"
    @save="emit('save')"
  >
          <WorkExperienceEvidenceInstructions :verified="currentWorkExperience?.experience_verified === 2" />

          <WorkExperienceDetailsFieldset
            :work-experience-year="workExperienceYear"
            :work-experience-short-description="workExperienceShortDescription"
            :work-experience-long-description="workExperienceLongDescription"
            :disabled="currentWorkExperience?.experience_verified === 2"
            @update:work-experience-year="emit('update:workExperienceYear', $event)"
            @update:work-experience-short-description="emit('update:workExperienceShortDescription', $event)"
            @update:work-experience-long-description="emit('update:workExperienceLongDescription', $event)"
            @work-experience-long-input-change="emit('workExperienceLongInputChange')"
          />

          <WorkExperienceAttachments
            :current-work-experience-attachments="currentWorkExperienceAttachments"
            :work-experience-attachment-delete-ids="workExperienceAttachmentDeleteIds"
            :show-work-experience-fine-uploader="showWorkExperienceFineUploader && currentWorkExperience?.experience_verified !== 2"
            :visible-work-experience-new-attachments="visibleWorkExperienceNewAttachments"
            :work-experience-upload-drop-active="workExperienceUploadDropActive"
            :work-experience-upload-alert-message="workExperienceUploadAlertMessage"
            :delete-disabled="currentWorkExperience?.experience_verified === 2"
            :upload-thumbnail-url="uploadThumbnailUrl"
            @stage-work-experience-attachment-delete="emit('stageWorkExperienceAttachmentDelete', $event)"
            @work-experience-upload-drag-enter="emit('workExperienceUploadDragEnter', $event)"
            @work-experience-upload-drag-over="emit('workExperienceUploadDragOver', $event)"
            @work-experience-upload-drag-leave="emit('workExperienceUploadDragLeave', $event)"
            @work-experience-upload-drop="emit('workExperienceUploadDrop', $event)"
            @trigger-upload-input="emit('triggerUploadInput', $event)"
            @upload-button-keydown="emit('uploadButtonKeydown', $event)"
            @upload-button-hover="(event, hover) => emit('uploadButtonHover', event, hover)"
            @upload-button-focus="(event, focused) => emit('uploadButtonFocus', event, focused)"
            @work-experience-attachment-selected="emit('workExperienceAttachmentSelected', $event)"
            @cancel-staged-upload="emit('cancelStagedUpload', $event)"
            @retry-work-experience-attachment="emit('retryWorkExperienceAttachment', $event)"
            @delete-staged-work-experience-attachment="emit('deleteStagedWorkExperienceAttachment', $event)"
            @close-work-experience-upload-alert="emit('closeWorkExperienceUploadAlert')"
          />

          <ModalAlert alert-id="WorkExperienceModalError" :alert-tone="alertTone" :message="errorMessage" />
  </CrudModalShell>
</template>
